import React, { useEffect, useMemo } from 'react';
import ImageLayout, { Container } from 'components/ImageLayout';
import { Formik } from 'formik';
import styled from 'styled-components';
import useOnSubmit from 'hooks/useOnSubmit';
import api from 'libs/api';
import { concatTZDateTimeFromObject } from 'libs/dateTimeHelpers';
import Form from './Form';
import useGetPublicAccount from 'hooks/common/useGetPublicAccount';
import { useNavigate, useParams } from 'react-router-dom';
import Flex from 'components/Flex';
import { Notice } from 'components/Notices';
import { numberNotRequired, numberRequired, stringRequired } from 'libs/validations';
import * as Yup from 'yup';
import { endOfDay, startOfDay } from 'date-fns';
import { IPolicy } from 'hooks/useDropdownMenu';

export interface IReservationValues {
  id?: number;
  first_name: string;
  last_name: string;
  phone_number: string;
  guest?: 'visitor' | 'vendor';
  license_plate: string;
  unit_id: number | undefined;
  start_date?: Date;
  end_date?: Date;
  start_time_hours?: string;
  start_time_minutes?: string;
  end_time_hours?: string;
  end_time_minutes?: string;
  start_time?: string;
  end_time?: string;
  parking_spot_id?: number;
  location_country_id?: number;
  location_region_id?: number;
  company?: string;
  status?: 'pending' | 'approved' | 'declined';
  policy?: IPolicy;
}

export const initialValues = {
  first_name: '',
  last_name: '',
  phone_number: '',
  license_plate: '',
  guest: undefined,
  building_id: undefined,
  unit_id: undefined,
  parking_spot_id: undefined,
  location_country_id: undefined,
  location_region_id: undefined,
  start_date: undefined,
  start_time: '',
  end_date: undefined,
  end_time: '',
  company: '',
};

const PublicReservationPage = () => {
  const { guid } = useParams();
  const { account, isLoading } = useGetPublicAccount(guid);
  const navigate = useNavigate();

  useEffect(() => {
    !account.parking_public_reservation && navigate('/404', { replace: true });
  }, [account]);

  const onSubmit = useOnSubmit<IReservationValues>({
    api: api.account.parking.public.reservations.create,
    successMessage: 'Reservation successfully created',
    onSuccess: () =>
      navigate(`/parking/reservation/${guid}/success`, {
        state: { parkingMustApproved: account.parking_must_approved },
      }),
    serializer: (values: IReservationValues) => {
      const start_time = concatTZDateTimeFromObject<IReservationValues>(
        values,
        ['start_date'],
        ['start_time']
      );
      const end_time = concatTZDateTimeFromObject<IReservationValues>(
        values,
        ['end_date'],
        ['end_time']
      );
      return { guid, resource: { ...values, start_time, end_time } };
    },
  });

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        first_name: stringRequired,
        last_name: stringRequired,
        phone_number: stringRequired,
        guest: stringRequired,
        license_plate: stringRequired,
        company: Yup.string()
          .nullable()
          .when('guest', (value: string, schama: any) => {
            return value === 'vendor'
              ? schama.required('Company name is required for vendors')
              : schama;
          }),
        building_id: numberNotRequired.when('guest', (value: string, schama: any) => {
          return account.parking_unit_must_provided && value === 'visitor'
            ? schama.required('Required')
            : schama;
        }),
        unit_id: numberNotRequired.when('guest', (value: string, schama: any) => {
          return account.parking_unit_must_provided && value === 'visitor'
            ? schama.required('Required')
            : schama;
        }),
        parking_spot_id: numberRequired.nullable(),
        location_country_id: numberRequired,
        location_region_id: Yup.number().nullable(),
        start_date: Yup.date()
          .transform((cv) => startOfDay(new Date(cv)))
          .required('Required'),
        end_date: Yup.date()
          .transform((cv) => endOfDay(new Date(cv)))
          .required('Required')
          .test(
            'end_date',
            'End time must be equal or greater then start time',
            function (end: Date) {
              const start = this.resolve(Yup.ref('start_date'));

              if (start && end) {
                return new Date(start).toISOString() <= end.toISOString();
              }

              return true;
            }
          ),
        start_time: stringRequired.transform((cv) => cv || ''),
        end_time: stringRequired.transform((cv) => cv || ''),
      }),
    [account.parking_unit_must_provided]
  );

  return (
    <>
      {!isLoading && (
        <ImageLayout direction="column">
          <Flex alignItems="center" direction="column" className="mt32 mb32">
            <h1 className="mb10">{account.name}</h1>
            <h2>Parking Registration</h2>
          </Flex>
          {account.parking_must_approved && (
            <Notice>
              This booking/reservation will need to be approved first before you can park in a
              visitor/guest parking spot.
            </Notice>
          )}
          <ContainerStyled>
            <Formik<IReservationValues>
              initialValues={initialValues}
              onSubmit={onSubmit}
              enableReinitialize={false}
              validationSchema={validationSchema}
            >
              {(props) => <Form account={account} guid={guid} {...props} />}
            </Formik>
          </ContainerStyled>
        </ImageLayout>
      )}
    </>
  );
};

export default PublicReservationPage;

const ContainerStyled = styled(Container)`
  color: ${(props) => props.theme.colors.dark};
  ${(props) => props.theme.media.xs`
    width: 100%;
  `}
`;
